import { useCallback, useRef } from 'react';

import { confirmUpload, createUploadUrl, uploadToS3 } from '@api';
import { AllowedDocumentContentType, DocumentUploadConfig, UploadCallbacks } from '@libs/types';

export const useDocumentUpload = <TDocument = string>(config: DocumentUploadConfig) => {
  const abortControllerRef = useRef<Map<string, AbortController>>(new Map());

  const uploadDocument = useCallback(
    async (
      file: File,
      metadata: Record<string, unknown>,
      callbacks: UploadCallbacks<TDocument>,
    ): Promise<string> => {
      const uploadId = crypto.randomUUID();

      const abortController = new AbortController();
      abortControllerRef.current.set(uploadId, abortController);

      // Notify immediately that upload has started with its ID
      callbacks.onStart?.(uploadId);

      try {
        // 1. Get Presigned URL From BackeEnd
        callbacks.onProgress(5);
        const uploadUrlPayload = {
          fileName: file.name,
          contentType: file.type as AllowedDocumentContentType,
          fileSize: file.size,
          ...metadata,
        };
        const { s3UploadUrl, s3Key, documentId } = await createUploadUrl(
          config.endpoints.createUrl,
          uploadUrlPayload,
          abortController.signal,
        );

        // 2. Upload To S3
        await uploadToS3(
          s3UploadUrl,
          file,
          (progress: number) => callbacks.onProgress(5 + Math.round(progress * 0.85)),
          abortController.signal,
        );

        let document;

        // 3. Confirm Upload With Backend (optional for stateless uploads)
        if (config.endpoints.confirm) {
          document = await confirmUpload(
            config.endpoints.confirm(documentId),
            {
              fileName: file.name,
              fileSize: file.size,
              contentType: file.type as AllowedDocumentContentType,
              s3Key,
              ...metadata,
            },
            abortController.signal,
          );
        }

        const result = document || documentId;

        callbacks.onProgress(100);
        callbacks.onSuccess(result as TDocument, { s3Key });

        return uploadId;
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          callbacks.onError('Upload failed. Please try again.');
        }
        throw error;
      } finally {
        abortControllerRef.current.delete(uploadId);
      }
    },

    [config],
  );

  const cancelUpload = useCallback((uploadId: string) => {
    const controller = abortControllerRef.current.get(uploadId);
    if (controller) {
      controller.abort();
      abortControllerRef.current.delete(uploadId);
    }
  }, []);

  const cancelAllUploads = useCallback(() => {
    abortControllerRef.current.forEach((controller) => controller.abort());
    abortControllerRef.current.clear();
  }, []);

  const hasActiveUploads = useCallback(() => {
    return abortControllerRef.current.size > 0;
  }, []);

  return { uploadDocument, cancelUpload, cancelAllUploads, hasActiveUploads };
};
